// ─────────────────────────────────────────────────────────────────
// Glass-i — Supplier Payment + Payment Voucher helpers (Stage 6P.7).
//
// Pure ESM module — exercised by the frontend node:test runner.
//
// Frontend mirror of backend Supplier Payment math + payload shaping.
// The backend
// (`backend/src/services/erp/supplierPayment/*`
//  — plus the shared accounting `voucherPosting.service.js`)
// remains the source of truth for:
//   - payment_no allocation (SPAY-YYYYMMDD-NNNNNN)
//   - supplier-payable ledger code resolution
//     (supplier-specific control → SUNDRY-CREDITORS fallback;
//      SUPPLIER-ADVANCE for ADVANCE / ON_ACCOUNT)
//   - payment-method-aware credit-side ledger resolution
//     (CASH → cash subtype; BANK / UPI / NEFT / RTGS / IMPS / CHEQUE
//      → bank subtype; OTHER → explicit paymentAccountId required)
//   - duplicate-block decisions (one voucher per payment_id)
//   - bill outstanding balance (bill_grand_total − Σ active prior
//     allocations)
//   - BILL_WISE vs ADVANCE vs ON_ACCOUNT payment-amount invariants
//   - supplier-payable register classification (UNPAID /
//     PARTIALLY_PAID / PAID / OVERPAID)
//
// These helpers exist only so the Stage 6P.7 preview / post UI can
// show consistent payment / allocation totals, status badges and
// capability gates while the operator reviews the draft. The API
// recomputes on post and is the final authority.
//
// Mirrors the style of:
//   - frontend/src/pages/erp/purchaseAccounting/purchaseAccountingHelpers.js
//   - backend/src/services/erp/supplierPayment/supplierPayment.constants.js
//     (enum lists — frontend mirrors are sealed with Object.freeze so
//     callers can't mutate the shared list by accident).
// ─────────────────────────────────────────────────────────────────


// ─────────────────────────────────────────────────────────────────
// Enum mirrors — kept aligned with backend
// services/erp/supplierPayment/supplierPayment.constants.js.
// Frozen so callers can't mutate the shared lists by accident.
// ─────────────────────────────────────────────────────────────────

export const PAYMENT_TYPES = Object.freeze([
  'BILL_WISE',
  'ADVANCE',
  'ON_ACCOUNT'
]);

export const PAYMENT_METHODS = Object.freeze([
  'BANK',
  'CASH',
  'UPI',
  'CHEQUE',
  'NEFT',
  'RTGS',
  'IMPS',
  'OTHER'
]);

export const STATUSES = Object.freeze([
  'DRAFT',
  'POSTED',
  'POSTING_FAILED',
  'CANCELLED'
]);

export const POSTING_STATUSES = Object.freeze([
  'NOT_POSTED',
  'POSTED',
  'POSTING_FAILED',
  'CANCELLED'
]);

export const ALLOCATION_STATUSES = Object.freeze([
  'ACTIVE',
  'CANCELLED'
]);

export const POSTING_LINE_TYPES = Object.freeze([
  'SUPPLIER_PAYABLE',
  'BANK_CASH'
]);

export const EVENT_TYPES = Object.freeze([
  'CREATED',
  'UPDATED',
  'POSTED',
  'POSTING_FAILED',
  'CANCELLED',
  'DUPLICATE_BLOCKED',
  'VOUCHER_CANCEL_FAILED'
]);

export const PAYABLE_BALANCE_STATUSES = Object.freeze([
  'UNPAID',
  'PARTIALLY_PAID',
  'PAID',
  'OVERPAID'
]);


// ─────────────────────────────────────────────────────────────────
// Private constants used by the math + classifier helpers.
// ─────────────────────────────────────────────────────────────────

const PAYMENT_TYPES_SET    = new Set(PAYMENT_TYPES);
const PAYMENT_METHODS_SET  = new Set(PAYMENT_METHODS);
const STATUSES_SET         = new Set(STATUSES);

// Half-paisa tolerance — matches backend BALANCE_EPSILON.
const BALANCE_EPSILON = 0.01;

// String constants — kept private to dodge accidental re-export drift.
const TYPE_BILL_WISE  = 'BILL_WISE';
const TYPE_ADVANCE    = 'ADVANCE';
const TYPE_ON_ACCOUNT = 'ON_ACCOUNT';

const STATUS_DRAFT          = 'DRAFT';
const STATUS_POSTED         = 'POSTED';
const STATUS_POSTING_FAILED = 'POSTING_FAILED';
const STATUS_CANCELLED      = 'CANCELLED';


// ─────────────────────────────────────────────────────────────────
// Tolerant numeric coercion — mirrors backend toNumber.
// Null / undefined / empty string / non-finite all collapse to 0.
// Never throws, never returns NaN.
// ─────────────────────────────────────────────────────────────────

function toNumber(value) {
  if (value === null || value === undefined) return 0;
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return 0;
    const n = Number(trimmed);
    return Number.isFinite(n) ? n : 0;
  }
  if (typeof value === 'boolean') return value ? 1 : 0;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

// Half-up rounding — defeats IEEE-754 surprises like 1.005 → 1.0049…
function roundHalfUp(value, decimals) {
  const n = toNumber(value);
  if (n === 0) return 0;
  const factor = Math.pow(10, decimals);
  const sign   = n < 0 ? -1 : 1;
  const abs    = Math.abs(n);
  const scaled = abs * factor + 1e-9;
  return (sign * Math.round(scaled)) / factor;
}

function round2(n) { return roundHalfUp(n, 2); }


// ─────────────────────────────────────────────────────────────────
// Snake / camel access — preview rows arrive from the API in camel,
// but ledger / posting rows arrive in both depending on whether they
// were freshly mapped or replayed from a server cache.
// We probe both and never mutate the caller.
// ─────────────────────────────────────────────────────────────────

function pick(obj, snake, camel, fallback) {
  if (!obj || typeof obj !== 'object') return fallback;
  if (snake in obj && obj[snake] !== undefined) return obj[snake];
  if (camel in obj && obj[camel] !== undefined) return obj[camel];
  return fallback;
}

function pickStringUpper(obj, snake, camel) {
  const v = pick(obj, snake, camel, '');
  if (v === null || v === undefined) return '';
  return String(v).trim().toUpperCase();
}

function pickString(obj, snake, camel) {
  const v = pick(obj, snake, camel, '');
  if (v === null || v === undefined) return '';
  return String(v).trim();
}


// ─────────────────────────────────────────────────────────────────
// Row normalisers — the API returns camelCase rows already, but the
// grid + drawer also consume rows replayed from local state or
// fixture data that may carry either casing. These normalisers return
// a canonical camelCase shape with widened numbers, never mutating
// the input.
//
// Anything we don't recognise is preserved on the way out so the
// grid can show columns we don't know about (e.g. future tenant
// extensions) without losing data.
// ─────────────────────────────────────────────────────────────────

export function normalizeSupplierPayment(row) {
  if (!row || typeof row !== 'object') return null;
  const out = Object.assign({}, row);

  out.id                       = pick(row, 'id', 'id', null);
  out.paymentNo                = pick(row, 'payment_no',   'paymentNo',   null);
  out.paymentDate              = pick(row, 'payment_date', 'paymentDate', null);

  out.paymentType              = pickStringUpper(row, 'payment_type',   'paymentType')   || null;
  out.paymentMethod            = pickStringUpper(row, 'payment_method', 'paymentMethod') || null;

  out.supplierId               = pick(row, 'supplier_id',        'supplierId',        null);
  out.supplierName             = pick(row, 'supplier_name',      'supplierName',      null);
  out.supplierGstin            = pick(row, 'supplier_gstin',     'supplierGstin',     null);
  out.supplierStateCode        = pick(row, 'supplier_state_code','supplierStateCode', null);
  out.companyStateCode         = pick(row, 'company_state_code', 'companyStateCode',  null);

  out.supplierLedgerAccountId   = pick(row, 'supplier_ledger_account_id',   'supplierLedgerAccountId',   null);
  out.supplierLedgerAccountCode = pick(row, 'supplier_ledger_account_code', 'supplierLedgerAccountCode', null);
  out.supplierLedgerAccountName = pick(row, 'supplier_ledger_account_name', 'supplierLedgerAccountName', null);

  out.paymentAccountId         = pick(row, 'payment_account_id',   'paymentAccountId',   null);
  out.paymentAccountCode       = pick(row, 'payment_account_code', 'paymentAccountCode', null);
  out.paymentAccountName       = pick(row, 'payment_account_name', 'paymentAccountName', null);

  out.instrumentNo             = pick(row, 'instrument_no',          'instrumentNo',         null);
  out.instrumentDate           = pick(row, 'instrument_date',        'instrumentDate',       null);
  out.bankReference            = pick(row, 'bank_reference',         'bankReference',        null);
  out.counterpartyBankName     = pick(row, 'counterparty_bank_name', 'counterpartyBankName', null);
  out.counterpartyBankBranch   = pick(row, 'counterparty_bank_branch', 'counterpartyBankBranch', null);

  out.paymentAmount            = toNumber(pick(row, 'payment_amount',     'paymentAmount',     0));
  out.allocatedAmount          = toNumber(pick(row, 'allocated_amount',   'allocatedAmount',   0));
  out.unallocatedAmount        = toNumber(pick(row, 'unallocated_amount', 'unallocatedAmount', 0));

  out.status                   = pickStringUpper(row, 'status',         'status')        || null;
  out.postingStatus            = pickStringUpper(row, 'posting_status', 'postingStatus') || null;

  out.voucherId                = pick(row, 'voucher_id', 'voucherId', null);
  out.voucherNo                = pick(row, 'voucher_no', 'voucherNo', null);

  out.failureReason            = pick(row, 'failure_reason',      'failureReason',      null);
  out.failureDetail            = pick(row, 'failure_detail',      'failureDetail',      null);
  out.cancellationReason       = pick(row, 'cancellation_reason', 'cancellationReason', null);

  out.postingReadyAt           = pick(row, 'posting_ready_at', 'postingReadyAt', null);
  out.postedAt                 = pick(row, 'posted_at',        'postedAt',       null);
  out.postedBy                 = pick(row, 'posted_by',        'postedBy',       null);
  out.failedAt                 = pick(row, 'failed_at',        'failedAt',       null);
  out.cancelledAt              = pick(row, 'cancelled_at',     'cancelledAt',    null);
  out.cancelledBy              = pick(row, 'cancelled_by',     'cancelledBy',    null);

  out.remarks                  = pick(row, 'remarks',    'remarks',   null);
  out.createdBy                = pick(row, 'created_by', 'createdBy', null);
  out.updatedBy                = pick(row, 'updated_by', 'updatedBy', null);
  out.createdAt                = pick(row, 'created_at', 'createdAt', null);
  out.updatedAt                = pick(row, 'updated_at', 'updatedAt', null);

  return out;
}

export function normalizeSupplierPaymentAllocation(row) {
  if (!row || typeof row !== 'object') return null;
  const out = Object.assign({}, row);

  out.id                    = pick(row, 'id', 'id', null);
  out.paymentId             = pick(row, 'payment_id', 'paymentId', null);
  out.lineNo                = toNumber(pick(row, 'line_no', 'lineNo', 0)) || 0;

  out.purchaseBillId        = pick(row, 'purchase_bill_id',     'purchaseBillId',     null);
  out.purchaseBillNo        = pick(row, 'purchase_bill_no',     'purchaseBillNo',     null);
  out.purchaseBillDate      = pick(row, 'purchase_bill_date',   'purchaseBillDate',   null);
  out.supplierInvoiceNo     = pick(row, 'supplier_invoice_no',  'supplierInvoiceNo',  null);
  out.supplierInvoiceDate   = pick(row, 'supplier_invoice_date','supplierInvoiceDate', null);

  out.billGrandTotal        = toNumber(pick(row, 'bill_grand_total',         'billGrandTotal',         0));
  out.billOutstandingBefore = toNumber(pick(row, 'bill_outstanding_before',  'billOutstandingBefore',  0));
  out.allocatedAmount       = toNumber(pick(row, 'allocated_amount',         'allocatedAmount',        0));
  out.billOutstandingAfter  = toNumber(pick(row, 'bill_outstanding_after',   'billOutstandingAfter',   0));

  out.status                = pickStringUpper(row, 'status', 'status') || null;
  out.cancelledAt           = pick(row, 'cancelled_at',         'cancelledAt',         null);
  out.cancelledBy           = pick(row, 'cancelled_by',         'cancelledBy',         null);
  out.cancellationReason    = pick(row, 'cancellation_reason',  'cancellationReason',  null);

  out.remarks               = pick(row, 'remarks',    'remarks',   null);
  out.createdAt             = pick(row, 'created_at', 'createdAt', null);
  out.updatedAt             = pick(row, 'updated_at', 'updatedAt', null);

  return out;
}

export function normalizeSupplierPayableRow(row) {
  if (!row || typeof row !== 'object') return null;
  const out = Object.assign({}, row);

  out.supplierId     = pick(row, 'supplier_id',   'supplierId',   null);
  out.supplierName   = pick(row, 'supplier_name', 'supplierName', null);
  out.supplierCode   = pick(row, 'supplier_code', 'supplierCode', null);
  out.supplierGstin  = pick(row, 'supplier_gstin','supplierGstin',null);

  out.purchaseBillId   = pick(row, 'purchase_bill_id',   'purchaseBillId',   null);
  out.purchaseBillNo   = pick(row, 'purchase_bill_no',   'purchaseBillNo',   null);
  out.purchaseBillDate = pick(row, 'purchase_bill_date', 'purchaseBillDate', null);

  // Optional posting cross-link for callers that pivot through the
  // accounting posting surface.
  out.postingId   = pick(row, 'posting_id',   'postingId',   null);
  out.postingNo   = pick(row, 'posting_no',   'postingNo',   null);
  out.postingDate = pick(row, 'posting_date', 'postingDate', null);

  out.payableAmount     = toNumber(pick(row, 'payable_amount',     'payableAmount',     0));
  out.paidAmount        = toNumber(pick(row, 'paid_amount',        'paidAmount',        0));
  out.outstandingAmount = toNumber(
    pick(row, 'outstanding_amount', 'outstandingAmount',
      // Derive when neither side sent a value.
      toNumber(pick(row, 'payable_amount', 'payableAmount', 0)) -
      toNumber(pick(row, 'paid_amount',    'paidAmount',    0))
    )
  );

  out.balanceStatus  = pickStringUpper(row, 'balance_status', 'balanceStatus') || null;
  out.dueDate        = pick(row, 'due_date', 'dueDate', null);
  out.ageDays        = toNumber(pick(row, 'age_days', 'ageDays', 0)) || 0;
  out.currency       = pick(row, 'currency', 'currency', null);

  return out;
}


// ─────────────────────────────────────────────────────────────────
// Supplier Payment payload builder — produces the body for
// POST /api/erp/supplier-payments and PUT /api/erp/supplier-payments/:id.
//
// Maps the camelCase form values rendered by the AntD form to the
// snake_case shape the backend Joi validator expects. Nullable / blank
// keys are stripped so an "empty" instrument field doesn't echo back
// as null in the audit logs. Allocations are normalised to the
// allocation-line shape the validator accepts.
// ─────────────────────────────────────────────────────────────────

export function buildSupplierPaymentPayload(formValues) {
  const v = formValues && typeof formValues === 'object' ? formValues : {};
  const out = {};

  // Payment date.
  const paymentDate = pick(v, 'payment_date', 'paymentDate', null);
  if (paymentDate !== null && paymentDate !== undefined) {
    if (paymentDate instanceof Date && !Number.isNaN(paymentDate.getTime())) {
      out.payment_date = paymentDate.toISOString().slice(0, 10);
    } else {
      const s = String(paymentDate).trim();
      if (s) out.payment_date = s;
    }
  }

  // Supplier identity.
  const supplierId = pick(v, 'supplier_id', 'supplierId', null);
  if (supplierId !== null && supplierId !== undefined && String(supplierId).trim() !== '') {
    out.supplier_id = String(supplierId).trim();
  }

  const supplierName = pickString(v, 'supplier_name', 'supplierName');
  if (supplierName) out.supplier_name = supplierName;

  const supplierGstin = pickStringUpper(v, 'supplier_gstin', 'supplierGstin');
  if (supplierGstin) out.supplier_gstin = supplierGstin;

  // Taxonomy.
  const paymentType = pickStringUpper(v, 'payment_type', 'paymentType');
  if (paymentType) out.payment_type = paymentType;

  const paymentMethod = pickStringUpper(v, 'payment_method', 'paymentMethod');
  if (paymentMethod) out.payment_method = paymentMethod;

  // Credit-side ledger resolver inputs.
  const paymentAccountId = pick(v, 'payment_account_id', 'paymentAccountId', null);
  if (paymentAccountId !== null && paymentAccountId !== undefined &&
      String(paymentAccountId).trim() !== '') {
    out.payment_account_id = String(paymentAccountId).trim();
  }

  const paymentAccountName = pickString(v, 'payment_account_name', 'paymentAccountName');
  if (paymentAccountName) out.payment_account_name = paymentAccountName;

  // Instrument-level identifiers.
  const referenceNo = pickString(v, 'reference_no', 'referenceNo');
  if (referenceNo) out.reference_no = referenceNo;

  const referenceDate = pick(v, 'reference_date', 'referenceDate', null);
  if (referenceDate !== null && referenceDate !== undefined) {
    if (referenceDate instanceof Date && !Number.isNaN(referenceDate.getTime())) {
      out.reference_date = referenceDate.toISOString().slice(0, 10);
    } else {
      const s = String(referenceDate).trim();
      if (s) out.reference_date = s;
    }
  }

  const bankName = pickString(v, 'bank_name', 'bankName');
  if (bankName) out.bank_name = bankName;

  const chequeNo = pickString(v, 'cheque_no', 'chequeNo');
  if (chequeNo) out.cheque_no = chequeNo;

  const utrNo = pickString(v, 'utr_no', 'utrNo');
  if (utrNo) out.utr_no = utrNo;

  // Money.
  const paymentAmount = pick(v, 'payment_amount', 'paymentAmount', undefined);
  if (paymentAmount !== undefined && paymentAmount !== null && paymentAmount !== '') {
    out.payment_amount = round2(toNumber(paymentAmount));
  }

  const roundOff = pick(v, 'round_off', 'roundOff', undefined);
  if (roundOff !== undefined && roundOff !== null && roundOff !== '') {
    out.round_off = round2(toNumber(roundOff));
  }

  const remarks = pick(v, 'remarks', 'remarks', null);
  if (remarks !== null && remarks !== undefined) {
    const s = String(remarks).trim();
    if (s) out.remarks = s;
  }

  // Allocations — only included for BILL_WISE. For ADVANCE /
  // ON_ACCOUNT we send an empty array so the backend validator's
  // "default([])" sees the explicit empty.
  const allocationsRaw = pick(v, 'allocations', 'allocations', []);
  if (paymentType === TYPE_BILL_WISE && Array.isArray(allocationsRaw) && allocationsRaw.length > 0) {
    out.allocations = allocationsRaw
      .filter((a) => a && typeof a === 'object')
      .map((a) => {
        const line = {};
        const billId = pick(a, 'purchase_bill_id', 'purchaseBillId', null);
        if (billId !== null && billId !== undefined && String(billId).trim() !== '') {
          line.purchase_bill_id = String(billId).trim();
        }
        const postingId = pick(a, 'purchase_accounting_posting_id', 'purchaseAccountingPostingId', null);
        if (postingId !== null && postingId !== undefined && String(postingId).trim() !== '') {
          line.purchase_accounting_posting_id = String(postingId).trim();
        }
        const amt = pick(a, 'allocation_amount', 'allocationAmount', undefined);
        if (amt !== undefined && amt !== null && amt !== '') {
          line.allocation_amount = round2(toNumber(amt));
        }
        const aRemarks = pick(a, 'remarks', 'remarks', null);
        if (aRemarks !== null && aRemarks !== undefined) {
          const s = String(aRemarks).trim();
          if (s) line.remarks = s;
        }
        return line;
      });
  } else {
    out.allocations = [];
  }

  return out;
}


// ─────────────────────────────────────────────────────────────────
// Allocation totals — the preview footer shows allocated + un-
// allocated amounts as the operator adjusts the per-bill grid.
// The backend recomputes server-side; this helper exists so the
// footer stays in sync as the operator scrolls.
//
// Tolerant: ignores non-array input, ignores non-object rows, never
// throws. Totals are rounded half-up to 2dp (rupee precision).
// Cancelled allocations are excluded from the running total.
// ─────────────────────────────────────────────────────────────────

export function calculatePaymentAllocationTotals(allocations) {
  let allocated = 0;

  if (Array.isArray(allocations)) {
    for (const a of allocations) {
      if (!a || typeof a !== 'object') continue;
      const status = pickStringUpper(a, 'status', 'status');
      if (status === 'CANCELLED') continue;
      allocated += toNumber(pick(a, 'allocation_amount', 'allocationAmount', 0));
    }
  }

  const allocatedRounded = round2(allocated);
  return {
    allocatedAmount:   allocatedRounded,
    // Unallocated isn't computable without the header amount; the
    // caller passes a synthetic { allocation_amount: paymentAmount }
    // row OR consumes summarizeSupplierPayment for that derivation.
    // Default to 0 so callers using just this helper see the running
    // allocated sum.
    unallocatedAmount: 0
  };
}


// ─────────────────────────────────────────────────────────────────
// Payment payload shape validator — mirrors the backend Joi shape
// in spirit but stays UI-friendly. Returns { ok, errors:[{path,
// message}] } so the form can render inline field errors without
// throwing.
// ─────────────────────────────────────────────────────────────────

export function validateSupplierPayment(payload) {
  const errors = [];

  if (!payload || typeof payload !== 'object') {
    return { ok: false, errors: [{ path: '', message: 'Payload is required.' }] };
  }

  const paymentDate = pick(payload, 'payment_date', 'paymentDate', null);
  if (paymentDate === null || paymentDate === undefined || String(paymentDate).trim() === '') {
    errors.push({ path: 'payment_date', message: 'Payment date is required.' });
  }

  const supplierName = pickString(payload, 'supplier_name', 'supplierName');
  if (!supplierName) {
    errors.push({ path: 'supplier_name', message: 'Supplier name is required.' });
  }

  const paymentType = pickStringUpper(payload, 'payment_type', 'paymentType');
  if (!paymentType) {
    errors.push({ path: 'payment_type', message: 'Payment type is required.' });
  } else if (!PAYMENT_TYPES_SET.has(paymentType)) {
    errors.push({
      path: 'payment_type',
      message: `Payment type must be one of: ${PAYMENT_TYPES.join(', ')}.`
    });
  }

  const paymentMethod = pickStringUpper(payload, 'payment_method', 'paymentMethod');
  if (!paymentMethod) {
    errors.push({ path: 'payment_method', message: 'Payment method is required.' });
  } else if (!PAYMENT_METHODS_SET.has(paymentMethod)) {
    errors.push({
      path: 'payment_method',
      message: `Payment method must be one of: ${PAYMENT_METHODS.join(', ')}.`
    });
  }

  const paymentAccountName = pickString(payload, 'payment_account_name', 'paymentAccountName');
  if (!paymentAccountName) {
    errors.push({ path: 'payment_account_name', message: 'Payment account name is required.' });
  }

  const paymentAmountRaw = pick(payload, 'payment_amount', 'paymentAmount', undefined);
  const paymentAmount = toNumber(paymentAmountRaw);
  if (paymentAmountRaw === undefined || paymentAmountRaw === null || paymentAmountRaw === '') {
    errors.push({ path: 'payment_amount', message: 'Payment amount is required.' });
  } else if (!(paymentAmount > 0)) {
    errors.push({ path: 'payment_amount', message: 'Payment amount must be greater than zero.' });
  }

  // Payment-type vs allocations invariant.
  const allocations = pick(payload, 'allocations', 'allocations', []);
  const allocationsArr = Array.isArray(allocations) ? allocations : [];
  if (paymentType === TYPE_BILL_WISE) {
    if (allocationsArr.length === 0) {
      errors.push({
        path: 'allocations',
        message: 'BILL_WISE payments must have at least one allocation.'
      });
    }
  } else if (paymentType === TYPE_ADVANCE || paymentType === TYPE_ON_ACCOUNT) {
    if (allocationsArr.length > 0) {
      errors.push({
        path: 'allocations',
        message: `${paymentType} payments cannot carry allocations.`
      });
    }
  }

  return { ok: errors.length === 0, errors };
}


// ─────────────────────────────────────────────────────────────────
// Per-allocation validator. Outstanding is the bill's current
// outstanding (post any prior active allocations). The header-level
// validateSupplierPayment is the right place to check sum invariants;
// this helper only polices a single allocation row.
//
// Returns { ok, errors:[{ path, message }] }.
// ─────────────────────────────────────────────────────────────────

export function validateSupplierPaymentAllocation(allocation, outstanding) {
  const errors = [];

  if (!allocation || typeof allocation !== 'object') {
    return { ok: false, errors: [{ path: '', message: 'Allocation is required.' }] };
  }

  const billId    = pick(allocation, 'purchase_bill_id', 'purchaseBillId', null);
  const postingId = pick(allocation, 'purchase_accounting_posting_id', 'purchaseAccountingPostingId', null);
  if (
    (billId === null || billId === undefined || String(billId).trim() === '') &&
    (postingId === null || postingId === undefined || String(postingId).trim() === '')
  ) {
    errors.push({
      path: 'purchase_bill_id',
      message: 'Allocation must reference a purchase bill or accounting posting.'
    });
  }

  const amountRaw = pick(allocation, 'allocation_amount', 'allocationAmount', undefined);
  const amount = toNumber(amountRaw);
  if (amountRaw === undefined || amountRaw === null || amountRaw === '') {
    errors.push({ path: 'allocation_amount', message: 'Allocation amount is required.' });
  } else if (!(amount > 0)) {
    errors.push({
      path: 'allocation_amount',
      message: 'Allocation amount must be greater than zero.'
    });
  }

  // Outstanding check — only when outstanding is a finite number.
  if (typeof outstanding === 'number' && Number.isFinite(outstanding)) {
    if (amount - outstanding > BALANCE_EPSILON) {
      errors.push({
        path: 'allocation_amount',
        message:
          `Allocation amount (${round2(amount)}) exceeds bill outstanding (${round2(outstanding)}).`
      });
    }
  }

  return { ok: errors.length === 0, errors };
}


// ─────────────────────────────────────────────────────────────────
// Cross-row guards — true/false predicates the form can use to
// disable the Save / Post CTA. Mirror the backend cross-field guards.
// ─────────────────────────────────────────────────────────────────

export function validateAllSameSupplier(allocations, supplierId) {
  if (!Array.isArray(allocations) || allocations.length === 0) return true;
  if (!supplierId) return true;

  const sid = String(supplierId);
  for (const a of allocations) {
    if (!a || typeof a !== 'object') continue;
    const allocSupplierId = pick(a, 'supplier_id', 'supplierId', null);
    if (allocSupplierId && String(allocSupplierId) !== sid) {
      return false;
    }
  }
  return true;
}

export function validatePaymentMatchesAllocated(payment) {
  if (!payment || typeof payment !== 'object') return false;

  const paymentType = pickStringUpper(payment, 'payment_type', 'paymentType');
  if (paymentType !== TYPE_BILL_WISE) {
    // ADVANCE / ON_ACCOUNT have no allocations — invariant is
    // satisfied by definition.
    return true;
  }

  const amount = toNumber(pick(payment, 'payment_amount', 'paymentAmount', 0));
  const allocations = pick(payment, 'allocations', 'allocations', []);
  const { allocatedAmount } = calculatePaymentAllocationTotals(
    Array.isArray(allocations) ? allocations : []
  );

  return Math.abs(round2(amount) - allocatedAmount) <= BALANCE_EPSILON;
}


// ─────────────────────────────────────────────────────────────────
// Capability gates — drive Edit / Post / Cancel / Delete CTAs in the
// drawer + grid. The backend remains the authority; these guards hide
// or disable CTAs for operators who clearly can't succeed.
//
//   Edit:    DRAFT or POSTING_FAILED
//   Post:    DRAFT or POSTING_FAILED
//   Cancel:  POSTED or POSTING_FAILED
//   Delete:  DRAFT and no downstream voucher
// ─────────────────────────────────────────────────────────────────

function normalizeStatus(status) {
  if (!status) return '';
  return String(status).trim().toUpperCase();
}

export function canEditSupplierPayment(status) {
  const key = normalizeStatus(status);
  return key === STATUS_DRAFT || key === STATUS_POSTING_FAILED;
}

export function canPostSupplierPayment(status) {
  const key = normalizeStatus(status);
  return key === STATUS_DRAFT || key === STATUS_POSTING_FAILED;
}

export function canCancelSupplierPayment(status) {
  const key = normalizeStatus(status);
  return key === STATUS_POSTED || key === STATUS_POSTING_FAILED;
}

export function canDeleteSupplierPayment(status, hasVoucher) {
  const key = normalizeStatus(status);
  if (key !== STATUS_DRAFT) return false;
  return !hasVoucher;
}


// ─────────────────────────────────────────────────────────────────
// Status labels + tag colours. Colours align with the AntD Tag
// palette guidance in docs/23-antd-component-standards.md so the
// payment grids match the rest of the control-plane chrome.
// ─────────────────────────────────────────────────────────────────

const PAYMENT_STATUS_LABELS = {
  DRAFT:          'Draft',
  POSTED:         'Posted',
  POSTING_FAILED: 'Posting Failed',
  CANCELLED:      'Cancelled'
};

const PAYMENT_STATUS_COLORS = {
  DRAFT:          'default',
  POSTED:         'success',
  POSTING_FAILED: 'error',
  CANCELLED:      'default'
};

export function paymentStatusLabel(status) {
  if (!status) return '';
  const key = normalizeStatus(status);
  return PAYMENT_STATUS_LABELS[key] || key;
}

export function paymentStatusColor(status) {
  if (!status) return 'default';
  const key = normalizeStatus(status);
  return PAYMENT_STATUS_COLORS[key] || 'default';
}

const POSTING_STATUS_LABELS = {
  NOT_POSTED:     'Not Posted',
  POSTED:         'Posted',
  POSTING_FAILED: 'Posting Failed',
  CANCELLED:      'Cancelled'
};

const POSTING_STATUS_COLORS = {
  NOT_POSTED:     'default',
  POSTED:         'success',
  POSTING_FAILED: 'error',
  CANCELLED:      'default'
};

export function postingStatusLabel(status) {
  if (!status) return '';
  const key = normalizeStatus(status);
  return POSTING_STATUS_LABELS[key] || key;
}

export function postingStatusColor(status) {
  if (!status) return 'default';
  const key = normalizeStatus(status);
  return POSTING_STATUS_COLORS[key] || 'default';
}


// ─────────────────────────────────────────────────────────────────
// Payment-method labels — human-readable text for the method enum.
// ─────────────────────────────────────────────────────────────────

const PAYMENT_METHOD_LABELS = {
  BANK:   'Bank Transfer',
  CASH:   'Cash',
  UPI:    'UPI',
  CHEQUE: 'Cheque',
  NEFT:   'NEFT',
  RTGS:   'RTGS',
  IMPS:   'IMPS',
  OTHER:  'Other'
};

export function paymentMethodLabel(method) {
  if (!method) return '';
  const key = normalizeStatus(method);
  return PAYMENT_METHOD_LABELS[key] || key;
}


// ─────────────────────────────────────────────────────────────────
// summarizeSupplierPayment — single-glance summary for the payment
// detail card. Returns the allocations array, the allocated total,
// the unallocated balance (payment_amount − allocated), the voucher
// number (when posted), and the payment status.
//
// Tolerant: ignores non-object input, never throws.
// ─────────────────────────────────────────────────────────────────

export function summarizeSupplierPayment(payment) {
  const out = {
    allocations:    [],
    allocated:      0,
    unallocated:    0,
    voucherNo:      null,
    paymentStatus:  null
  };

  if (!payment || typeof payment !== 'object') return out;

  const allocationsRaw = pick(payment, 'allocations', 'allocations', []);
  const allocations = Array.isArray(allocationsRaw) ? allocationsRaw : [];
  out.allocations = allocations;

  const { allocatedAmount } = calculatePaymentAllocationTotals(allocations);
  out.allocated = allocatedAmount;

  const paymentAmount = toNumber(pick(payment, 'payment_amount', 'paymentAmount', 0));
  out.unallocated = round2(paymentAmount - allocatedAmount);

  out.voucherNo     = pick(payment, 'voucher_no', 'voucherNo', null) || null;
  out.paymentStatus = pickStringUpper(payment, 'status', 'status') || null;

  return out;
}


// ─────────────────────────────────────────────────────────────────
// Sanity references — keep the importer pattern at the top of this
// module honest. Touching these here keeps a future refactor from
// silently dropping an export.
// ─────────────────────────────────────────────────────────────────
void PAYMENT_TYPES_SET;
void PAYMENT_METHODS_SET;
void STATUSES_SET;
